// src/components/CreateAlarmDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { createNotification } from '../services/notificationService';
import { CreateNotificationPayload } from '../types/church';
import { useAuth } from '../hooks/useAuth';

interface CreateAlarmDialogProps {
  open: boolean;
  onClose: () => void;
  eventId?: number | null;  // Optionnel - peut créer une alarme sans événement
  onSuccess: () => void;
}

const CreateAlarmDialog: React.FC<CreateAlarmDialogProps> = ({
  open,
  onClose,
  eventId = null,  // Par défaut null si non fourni
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { error: showError } = useNotification();
  const { authState } = useAuth();
  const [type, setType] = useState<'push' | 'sms' | 'email' | 'in_app'>('push');
  const [message, setMessage] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      showError(t('dialogs.createAlarm.messageRequired'));
      return;
    }

    if (!scheduledAt) {
      showError(t('dialogs.createAlarm.timeRequired'));
      return;
    }

    const scheduledDate = new Date(scheduledAt);
    const now = new Date();
    if (scheduledDate < now) {
      showError(t('validation.datePast'));
      return;
    }

    if (!authState.user?.id) {
      showError(t('dialogs.createAlarm.notAuthenticated'));
      return;
    }

    try {
      setLoading(true);
      const payload: CreateNotificationPayload = {
        user_id: authState.user.id,
        type,
        message: message.trim(),
        scheduled_at: scheduledDate.toISOString(),
      };

      // Ajouter event_id seulement s'il est défini
      if (eventId) {
        payload.event_id = eventId;
      }

      await createNotification(payload);
      setMessage('');
      setScheduledAt('');
      setType('push');
      onSuccess();
    } catch (err: any) {
      console.error('Failed to create alarm:', err);
      showError(err.response?.data?.detail || t('dialogs.createAlarm.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setMessage('');
      setScheduledAt('');
      setType('push');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('dialogs.createAlarm.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t('dialogs.createAlarm.typeLabel')}</InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as any)}
              label={t('dialogs.createAlarm.typeLabel')}
              disabled={loading}
            >
              <MenuItem value="push">{t('admin.notifications.types.push')}</MenuItem>
              <MenuItem value="sms">{t('admin.notifications.types.sms')}</MenuItem>
              <MenuItem value="email">{t('admin.notifications.types.email')}</MenuItem>
              <MenuItem value="in_app">{t('admin.notifications.types.in_app')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={t('dialogs.createAlarm.messageLabel')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            disabled={loading}
            placeholder={t('dialogs.createAlarm.messagePlaceholder')}
          />

          <TextField
            label={t('dialogs.createAlarm.dateTimeLabel')}
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={t('dialogs.createAlarm.dateTimeHelper')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('common.creating') : t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAlarmDialog;
